import { Dtos, type ProConId, type SuretyAccountAttachmentFileType } from "@inrev/common";
import { Option } from "@inrev/core";
import { type DataNeeded } from "@portal/api/decisioning";
import { type CreateDataNeededInput } from "@portal/api/decisioning/admin";

type DataNeededType = Dtos.Decisioning.DataNeededType;
type FileType = SuretyAccountAttachmentFileType;
type UpdateRequest = Dtos.Decisioning.DataNeeded.Update.Request;

export type RequestDraft = {
	type: DataNeededType;
	/** File types picked for a Document. Kept while the type is switched, but only a Document sends them. */
	fileTypes: readonly FileType[];
	/** Shown as the request's title. A Document's starts as its first file type. */
	title: string;
	/** Shared with the assignee. */
	description: string;
	rationale: string;
	assignee: Dtos.Decisioning.DataNeededAssignee;
	importance: Dtos.Decisioning.DataNeededImportance;
	proConId: ProConId | null;
};

const NO_FILE_TYPES: readonly FileType[] = [];

export const emptyRequestDraft = (linkedProConId: ProConId | null = null): RequestDraft => ({
	type: "Document",
	fileTypes: NO_FILE_TYPES,
	title: "",
	description: "",
	rationale: "",
	assignee: "Agent",
	importance: 2,
	proConId: linkedProConId,
});

export const requestDraftFromItem = (item: DataNeeded): RequestDraft => ({
	type: item.type,
	fileTypes: item.fileTypes,
	title: item.title,
	description: item.description ?? "",
	rationale: item.rationale,
	assignee: item.assignee,
	importance: item.importance,
	proConId: item.proConId,
});

/** Rebuilds the sheet's draft from a create that failed, so reopening keeps what was typed. */
export const requestDraftFromCreateInput = (input: CreateDataNeededInput): RequestDraft => ({
	type: input.type,
	fileTypes: input.fileTypes,
	title: input.title,
	description: input.description ?? "",
	rationale: input.rationale,
	assignee: input.assignee,
	importance: input.importance,
	proConId: input.proConId ?? null,
});

/** The label of the first picked file type in picker order, which a Document's title starts as. */
const leadingFileTypeLabel = (fileTypes: readonly FileType[]): string =>
	Option.mapOrElse(
		Dtos.Decisioning.inFileTypePickerOrder(fileTypes)[0],
		Dtos.Decisioning.fileTypeLabel,
		"",
	);

/**
 * Picking file types fills in the title with the first one, and keeps following the first while
 * the underwriter has not written their own.
 */
export const withPickedFileTypes = (
	draft: RequestDraft,
	fileTypes: readonly FileType[],
): RequestDraft => {
	const titleIsDefault =
		draft.title.trim() === "" || draft.title === leadingFileTypeLabel(draft.fileTypes);
	return {
		...draft,
		fileTypes,
		title: titleIsDefault ? leadingFileTypeLabel(fileTypes) : draft.title,
	};
};

export const REQUEST_TITLE_LABELS: Record<DataNeededType, string> = {
	Document: "File types",
	Question: "What do you need to know?",
	Task: "What needs to be done?",
};

/** The first thing missing from the draft, in the order the sheet asks for it. */
export const requestDraftIssue = (draft: RequestDraft): string | null => {
	if (draft.type === "Document" && draft.fileTypes.length === 0) return "Choose a file type.";
	if (draft.title.trim() === "")
		return draft.type === "Document" ? "Add a title." : "Say what you need.";
	if (draft.rationale.trim() === "") return "Add a rationale.";
	return null;
};

const isSameFileTypes = (a: readonly FileType[], b: readonly FileType[]) => {
	const aFileTypes = new Set(a);
	return a.length === b.length && b.every((fileType) => aFileTypes.has(fileType));
};

/** Whether the draft differs from where it started; picking the same file types again is no change. */
export const isRequestDraftChanged = (draft: RequestDraft, initial: RequestDraft): boolean =>
	(Object.keys(draft) as (keyof RequestDraft)[]).some((key) =>
		key === "fileTypes"
			? !isSameFileTypes(draft.fileTypes, initial.fileTypes)
			: draft[key] !== initial[key],
	);

const blankToNull = (value: string): string | null => {
	const trimmed = value.trim();
	return trimmed === "" ? null : trimmed;
};

/** What the request asks for: a Document's file types in picker order, none for a Question or Task. */
const fileTypesToSend = (draft: RequestDraft): FileType[] =>
	draft.type === "Document" ? Dtos.Decisioning.inFileTypePickerOrder(draft.fileTypes) : [];

export const toCreateInput = (draft: RequestDraft): CreateDataNeededInput => ({
	assignee: draft.assignee,
	type: draft.type,
	fileTypes: fileTypesToSend(draft),
	title: draft.title.trim(),
	rationale: draft.rationale.trim(),
	description: blankToNull(draft.description),
	importance: draft.importance,
	proConId: draft.proConId,
});

/** Only the fields that differ from the saved item, shaped for the PATCH body. */
export const toUpdateChanges = (draft: RequestDraft, item: DataNeeded): UpdateRequest => {
	const fileTypes = fileTypesToSend(draft);
	const typeOrFileTypesChanged =
		draft.type !== item.type || !isSameFileTypes(fileTypes, item.fileTypes);
	const title = draft.title.trim();
	const description = blankToNull(draft.description);
	const rationale = draft.rationale.trim();
	return {
		...(typeOrFileTypesChanged && { type: draft.type, fileTypes }),
		...(title !== item.title && { title }),
		...(description !== item.description && { description }),
		...(rationale !== item.rationale && { rationale }),
		...(draft.assignee !== item.assignee && { assignee: draft.assignee }),
		...(draft.importance !== item.importance && { importance: draft.importance }),
		...(draft.proConId === null && item.proConId !== null && { proConId: null }),
	};
};
